import type { VisionClient } from "../brain/vision-client"
import type { Box } from "./framing"

// Finding the car for the smart camera: the detector's settings, following
// the same car from frame to frame, and sorting a gallery photo into whole
// car or close-up. The model itself (MediaPipe Tasks Vision,
// EfficientDet-Lite0, labels car / truck / bus, self-hosted) runs in the
// vision engine (brain/vision-engine.ts), in a worker.
//
// After warm-up a short speed test decides: fast → ~5 looks/s; slow → fewer;
// too slow (> PERF_GIVE_UP_MS) → no detector, and the camera stays guided.

export interface DetectedBox {
  box: Box
  score: number
  label: string
}

export const DETECTOR = {
  MODEL_URL: "/mediapipe/efficientdet_lite0_int8.tflite",
  SCORE_THRESHOLD: 0.35,
  MAX_RESULTS: 3,
  LABELS: ["car", "truck", "bus"],
  PERF_GIVE_UP_MS: 350,
  PERF_SLOW_MS: 150,
  MIN_INTERVAL_MS: 200,
  SLOW_INTERVAL_MS: 500,
} as const

/* ── Target tracking (pure) ───────────────────────────────────────────── */

export function iou(a: Box, b: Box): number {
  const x1 = Math.max(a.x, b.x)
  const y1 = Math.max(a.y, b.y)
  const x2 = Math.min(a.x + a.w, b.x + b.w)
  const y2 = Math.min(a.y + a.h, b.y + b.h)
  const inter = Math.max(0, x2 - x1) * Math.max(0, y2 - y1)
  const union = a.w * a.h + b.w * b.h - inter
  return union > 0 ? inter / union : 0
}

const KEEP_IOU = 0.3
const SMOOTH = 0.5

// Which detection is "the car"? Keep following the one we had (overlap), else
// the biggest, most certain, most central one — so a car parked behind never
// steals the brackets. Smoothed so the brackets glide instead of jitter.
export function pickTarget(found: DetectedBox[], prev: Box | null): { box: Box; score: number } | null {
  if (found.length === 0) return null
  let chosen: DetectedBox | null = null
  if (prev) {
    let best = 0
    for (const d of found) {
      const o = iou(d.box, prev)
      if (o > best) {
        best = o
        chosen = d
      }
    }
    if (best <= KEEP_IOU) chosen = null
  }
  if (!chosen) {
    let bestScore = -1
    for (const d of found) {
      const cx = d.box.x + d.box.w / 2
      const cy = d.box.y + d.box.h / 2
      const central = 1 - Math.min(1, Math.hypot(cx - 0.5, cy - 0.5) / 0.7)
      const value = d.box.w * d.box.h * d.score * (0.25 + central)
      if (value > bestScore) {
        bestScore = value
        chosen = d
      }
    }
  }
  if (!chosen) return null
  if (prev && iou(chosen.box, prev) > KEEP_IOU) {
    const lerp = (a: number, b: number) => a + (b - a) * SMOOTH
    return {
      box: {
        x: lerp(prev.x, chosen.box.x),
        y: lerp(prev.y, chosen.box.y),
        w: lerp(prev.w, chosen.box.w),
        h: lerp(prev.h, chosen.box.h),
      },
      score: chosen.score,
    }
  }
  return { box: chosen.box, score: chosen.score }
}

// Is a gallery photo a whole-car shot or a close-up? The photo is letterboxed
// into the square model input (no squashing). "wide" = a car fully in view
// and reasonably big; "close" = no car found, or a car cut off by the edges.
export async function classifyPhoto(
  vision: VisionClient,
  source: CanvasImageSource & { width: number; height: number },
): Promise<"wide" | "close"> {
  const size = 320
  const canvas = document.createElement("canvas")
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext("2d")
  if (!ctx) return "close"
  ctx.fillStyle = "#808080"
  ctx.fillRect(0, 0, size, size)
  const scale = Math.min(size / source.width, size / source.height)
  const w = source.width * scale
  const h = source.height * scale
  const ox = (size - w) / 2
  const oy = (size - h) / 2
  ctx.drawImage(source, ox, oy, w, h)
  const found = await vision.detect(await createImageBitmap(canvas))
  const best = found.sort((a, b) => b.box.w * b.box.h * b.score - a.box.w * a.box.h * a.score)[0]
  if (!best) return "close"
  // Back to the photo's own 0–1 space.
  const x = (best.box.x * size - ox) / w
  const right = ((best.box.x + best.box.w) * size - ox) / w
  const y = (best.box.y * size - oy) / h
  const bottom = ((best.box.y + best.box.h) * size - oy) / h
  const cutOff = x < 0.02 || y < 0.02 || right > 0.98 || bottom > 0.98
  return !cutOff && right - x >= 0.3 ? "wide" : "close"
}
